import React, { useState, useMemo } from 'react';
import {
  Typography, Table, Tag, Button, Space, Card, Modal, Form,
  Select, DatePicker, Input, Empty, message, Tabs,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { snsPostsApi, clientsApi } from '../api/endpoints';
import { STATUS_LABELS, STATUS_COLORS, PLATFORM_LABELS, PLATFORM_COLORS } from '../constants/status';
import { SPACING, buildDateGroupMap } from '../styles/tokens';
import type { SnsPost, SnsPostStatus, Platform, Client, Store } from '../api/types';

const { Title } = Typography;

const STATUS_KEYS = Object.keys(STATUS_LABELS) as SnsPostStatus[];
const PLATFORM_KEYS = Object.keys(PLATFORM_LABELS) as Platform[];

const buildFilterParams = (searchParams: URLSearchParams): Record<string, string> => {
  const params: Record<string, string> = {};
  const status = searchParams.get('status');
  if (status && STATUS_KEYS.includes(status as SnsPostStatus)) {
    params.status = status;
  }
  const clientId = searchParams.get('client_id');
  if (clientId && /^\d+$/.test(clientId)) {
    params.client_id = clientId;
  }
  const platform = searchParams.get('platform');
  if (platform && PLATFORM_KEYS.includes(platform as Platform)) {
    params.platform = platform;
  }
  return params;
};

const statusTabItems = [
  { key: 'all', label: '全て' },
  ...STATUS_KEYS.map((key) => ({
    key,
    label: STATUS_LABELS[key],
  })),
];

const platformOptions = PLATFORM_KEYS.map((key) => ({
  value: key,
  label: PLATFORM_LABELS[key],
}));

const PostListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>();

  const filterParams = useMemo(
    () => buildFilterParams(searchParams),
    [searchParams],
  );

  const activeStatusTab = filterParams.status ?? 'all';

  const { data: posts, isLoading } = useQuery({
    queryKey: ['sns-posts', filterParams],
    queryFn: () =>
      snsPostsApi
        .list(Object.keys(filterParams).length > 0 ? filterParams : undefined)
        .then((r) => ((r.data as any).data ?? r.data) as SnsPost[]),
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list().then((r) => ((r.data as any).data ?? r.data) as Client[]),
  });

  const clientOptions = useMemo(
    () => (clients ?? []).map((c: Client) => ({ value: String(c.id), label: c.name })),
    [clients],
  );

  const createMutation = useMutation({
    mutationFn: (data: Partial<SnsPost>) => snsPostsApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['sns-posts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setModalOpen(false);
      form.resetFields();
      message.success('投稿タスクを作成しました');
      const newId = (res.data as any)?.data?.id ?? (res.data as any)?.id;
      if (newId) {
        navigate(`/posts/${newId}`);
      }
    },
    onError: () => message.error('作成に失敗しました'),
  });

  const dateGroupMap = useMemo(
    () => buildDateGroupMap(posts ?? []),
    [posts],
  );

  const selectedClient = clients?.find((c: Client) => c.id === selectedClientId);
  const stores: Store[] = selectedClient?.stores ?? [];

  const handleStatusTabChange = (key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'all') {
        next.delete('status');
      } else {
        next.set('status', key);
      }
      return next;
    });
  };

  const handleClientFilterChange = (value: string | undefined) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('client_id', value);
      } else {
        next.delete('client_id');
      }
      return next;
    });
  };

  const handlePlatformFilterChange = (value: string | undefined) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('platform', value);
      } else {
        next.delete('platform');
      }
      return next;
    });
  };

  const columns = [
    {
      title: 'ステータス', dataIndex: 'status', key: 'status', width: 150,
      render: (status: SnsPostStatus) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    { title: '日付', dataIndex: 'scheduled_date', key: 'date', width: 120 },
    {
      title: '店舗', key: 'store',
      render: (_: unknown, record: SnsPost) => record.store?.name ?? '-',
    },
    {
      title: 'PF', dataIndex: 'platform', key: 'platform', width: 120,
      render: (platform: Platform) => (
        <Tag color={PLATFORM_COLORS[platform]}>{PLATFORM_LABELS[platform]}</Tag>
      ),
    },
    { title: '原稿', dataIndex: 'caption', key: 'caption', ellipsis: true },
    {
      title: '写真', key: 'files', width: 80,
      render: (_: unknown, record: SnsPost) => record.sns_post_files?.length ?? 0,
    },
  ];

  return (
    <div>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACING.md }}>
        <Title level={4} style={{ margin: 0 }}>投稿管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          新規投稿
        </Button>
      </Space>

      <Tabs
        activeKey={activeStatusTab}
        items={statusTabItems}
        onChange={handleStatusTabChange}
        style={{ marginBottom: SPACING.md }}
      />

      <Space style={{ marginBottom: SPACING.md }} wrap>
        <Select
          placeholder="クライアント"
          allowClear
          style={{ minWidth: 200 }}
          value={filterParams.client_id ?? undefined}
          onChange={handleClientFilterChange}
          options={clientOptions}
        />
        <Select
          placeholder="プラットフォーム"
          allowClear
          style={{ minWidth: 160 }}
          value={filterParams.platform ?? undefined}
          onChange={handlePlatformFilterChange}
          options={platformOptions}
        />
      </Space>

      <Card>
        {!isLoading && (posts?.length || 0) === 0 ? (
          <Empty description="まだ投稿タスクがありません">
            <Button type="primary" onClick={() => setModalOpen(true)}>最初の投稿を作成</Button>
          </Empty>
        ) : (
          <Table
            dataSource={posts}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            rowClassName={(record) =>
              `clickable-row ${dateGroupMap[record.scheduled_date] ?? ''}`
            }
            onRow={(record) => ({
              onClick: () => navigate(`/posts/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      <Modal
        title="新規投稿タスク"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setSelectedClientId(undefined); }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            createMutation.mutate({
              store_id: values.store_id,
              platform: values.platform,
              scheduled_date: values.scheduled_date.format('YYYY-MM-DD'),
              caption: values.caption || '',
            });
          }}
        >
          <Form.Item label="クライアント" required>
            <Select
              placeholder="クライアントを選択"
              value={selectedClientId}
              onChange={(v) => {
                setSelectedClientId(v);
                form.setFieldValue('store_id', undefined);
              }}
              options={(clients || []).map((c: Client) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="store_id" label="店舗" rules={[{ required: true, message: '店舗を選択してください' }]}>
            <Select
              placeholder="店舗を選択"
              disabled={!selectedClientId}
              options={stores.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Form.Item name="platform" label="プラットフォーム" rules={[{ required: true, message: 'プラットフォームを選択してください' }]}>
            <Select
              placeholder="プラットフォームを選択"
              options={Object.entries(PLATFORM_LABELS).map(([k, v]) => ({ value: k, label: v }))}
            />
          </Form.Item>
          <Form.Item name="scheduled_date" label="予定日" rules={[{ required: true, message: '予定日を選択してください' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="caption" label="原稿（任意）">
            <Input.TextArea rows={3} placeholder="投稿の原稿を入力（後から編集可能）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PostListPage;
